import React, {useState} from 'react';
import {Button, Modal} from 'antd';
import {useStore} from '@/store/root-store';
import {stringToArray} from '@/utils/arrays';
import {faArrowRight} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {CustomTextarea} from '@/components/common-components/components/form/input/customTextarea';
import {keywordNewLineReg} from '@/constants/regex';
import {apiError} from '@/utils/api';
import {notification} from '@/utils/notification';
import styled from 'styled-components';
import {observer} from 'mobx-react';

interface Props {
  showAddFocusTermModal: boolean;
  setShowAddFocusTermModal: (value: boolean) => void;
}

const AddFocusTermModal = observer(({showAddFocusTermModal, setShowAddFocusTermModal}: Props) => {
  const [terms, setTerms] = useState<string>('');
  const {contentOptimizer: {currentPage: {addFocusTerms, isFocusTermsLoading}}} = useStore('');

  const onClose = () => {
    setShowAddFocusTermModal(false);
    setTerms('');
  };

  const handleTerms = (e: React.ChangeEvent<HTMLTextAreaElement>) =>{
    const value = e.target.value;
    setTerms(value.replaceAll(',', '\n'));
  };

  const handleAddFocusTerms = async () => {
    try {
      await addFocusTerms({terms: stringToArray(terms, keywordNewLineReg, ',' )});
      onClose();
      notification.success('', 'Focus terms added successfully');
    } catch (error) {
      const errorMessage = apiError(error) as string;
      notification.error('', errorMessage || 'Something went wrong please try again');
    }
  };

  return (
    <StyledModal
      width={460}
      visible={showAddFocusTermModal}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
    >
      <Title>Add focus terms</Title>
      <Desc>Provide your own terms and get usage recommendations to elevate your writing.</Desc>
      <TextArea>
        <CustomTextarea variant='light' minHeight={323} value={terms} onChange={handleTerms}/>
      </TextArea>
      <Footer>
        <button onClick={onClose} className='cancel-btn'>Cancel</button>
        <Button disabled={!terms.trim()} loading={isFocusTermsLoading} onClick={handleAddFocusTerms} className='add-btn'>Add <FontAwesomeIcon icon={faArrowRight} /></Button>
      </Footer>
    </StyledModal>
  );
});

export default AddFocusTermModal;

const StyledModal = styled(Modal)`
  .ant-modal-content{
    border-radius: 12px;
    background: var(--White, #FFF);
  }
`;

const Title = styled.div`
  color: var(--Text-black, var(--Black, #121212));
  font-family: Inter;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const Desc = styled.div`
  width: 370px;
  color: var(--Gray-dark, #4E5156);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-top: 4px;
`;

const TextArea = styled.div`
  margin-top: 22px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;

 .cancel-btn {
  color: var(--Gray-dark, #4E5156);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  background-color: transparent;
  border: none;
  cursor: pointer;
 }

 .add-btn {
  display: flex;
  width: 115px;
  height: 44px;
  /* padding: 13px 60px 14px 30px; */
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--Green, #2AC155);
  color: white;
  color: var(--White, var(--White, #FFF));
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
 }
 .ant-btn[disabled] {
  background: #f5f5f5 !important;
  color: rgba(0, 0, 0, 0.25) !important;
 }
`;
